import { assign, fromPromise, setup } from 'xstate';
import {
  beginProviderServiceNegotiation,
  searchProviderServiceListings,
} from '~/features/employer/provider-search/api';
import {
  autoSelectedStartKeys,
  earliestDateOptions,
  isStartDateOnly,
  selectionOptionKey,
} from '~/features/employer/provider-search/availabilityOptionUtils';
import {
  initialAvailabilityWindow,
  selectedAvailabilityEntryIds,
} from '~/features/employer/provider-search/ProviderAvailabilityOptions';
import type { ProviderServiceListingSearchItem } from '~/features/employer/provider-search/schemas';
import { isUncertainListingActionError } from '~/features/market/listing-search/errors';
import { beginNegotiation } from '~/features/market/negotiations/beginNegotiation';
import { MARKET_ACTION_TIMEOUT_MS, MARKET_READ_TIMEOUT_MS } from '~/features/market/requestPolicy';
import { withRequestDeadline } from '~/utils/requestDeadline';
import { SERVICE_OPPORTUNITIES_API as api } from './api';
import type { ServiceOpportunity } from './schemas';

type Scope = { responseId: number } | { listingId: number };
type Window = { start: string; end: string };
type Attempt = { responseId: number | null; listingId: number; entryIds: number[] };
type Context = Window & {
  responseId: number | null;
  discoveryListingId: number | null;
  recoveredResponseId: number | null;
  replayResponseId: number | null;
  discoveryResetRevision: number;
  confirmedResponseId: number | null;
  source: ServiceOpportunity | null;
  listing: ProviderServiceListingSearchItem | null;
  selectedKeys: string[];
  attempt: Attempt | null;
  open: boolean;
  error: string | null;
  uncertain: boolean;
  revision: number;
};

export const serviceBeginMachine = setup({
  types: {
    context: {} as Context,
    input: {} as Scope,
    events: {} as
      | { type: 'OPEN'; opportunity: ServiceOpportunity }
      | { type: 'CLOSE' }
      | { type: 'SEARCH'; window: Window }
      | { type: 'TOGGLE'; key: string }
      | { type: 'CHOOSE'; key: string }
      | { type: 'BEGIN' }
      | {
          type: 'BEGIN_DISCOVERY';
          listing: ProviderServiceListingSearchItem;
          selectedKeys: string[];
        }
      | { type: 'RETRY' }
      | { type: 'DISCARD' }
      | { type: 'CLEAR_CONFIRMATION' }
      | { type: 'REFRESH' },
  },
  actors: {
    search: fromPromise(
      ({
        input,
        signal,
      }: {
        input: { start: string; end?: string; listingId: number | null };
        signal: AbortSignal;
      }) =>
        withRequestDeadline(
          async (requestSignal) => {
            if (input.listingId === null) throw new Error('No service listing selected.');
            const result = await searchProviderServiceListings(
              {
                start: input.start,
                ...(input.end ? { end: input.end } : {}),
                providerServiceListingId: input.listingId,
              },
              1,
              1,
              requestSignal
            );
            if (result.data.some((row) => row.providerServiceListingId !== input.listingId)) {
              throw new Error('Availability listing scope changed.');
            }
            return result.data[0] ?? null;
          },
          MARKET_READ_TIMEOUT_MS,
          signal
        )
    ),
    begin: fromPromise(({ input, signal }: { input: Attempt | null; signal: AbortSignal }) =>
      withRequestDeadline(
        async (requestSignal) => {
          if (!input) throw new Error('No availability selection to submit.');
          const result =
            input.responseId === null
              ? await beginProviderServiceNegotiation(
                  input.listingId,
                  input.entryIds,
                  requestSignal
                )
              : await beginNegotiation(
                  {
                    listingType: 'SERVICE',
                    jobResponseId: input.responseId,
                    providerServiceListingId: input.listingId,
                    availabilityEntryIds: [...input.entryIds],
                  },
                  requestSignal
                );
          if (
            (input.responseId !== null && result.jobResponseId !== input.responseId) ||
            !Number.isSafeInteger(result.jobResponseId) ||
            result.jobResponseId < 1 ||
            result.responseType !== 'service' ||
            result.jobResponse.id !== result.jobResponseId ||
            result.jobResponse.listingType !== 'SERVICE' ||
            result.jobResponse.providerServiceListingId !== input.listingId ||
            !Number.isSafeInteger(result.negotiationId) ||
            result.negotiationId < 1
          ) {
            throw new Error('Negotiation acknowledgement scope changed.');
          }
          return result;
        },
        MARKET_ACTION_TIMEOUT_MS,
        signal
      )
    ),
    recover: fromPromise(({ input, signal }: { input: Attempt | null; signal: AbortSignal }) =>
      withRequestDeadline(
        async (requestSignal) => {
          if (!input) throw new Error('No negotiation attempt to recover.');
          if (input.responseId === null) {
            const page = await api.getPage('history', 1, requestSignal, input.listingId);
            const responses = page.data.filter((row) => row.viewerRole === 'RESPONDER');
            if (responses.some((row) => row.listing.id !== input.listingId)) {
              throw new Error('Service response listing scope changed.');
            }
            const newest = [...responses].sort((a, b) => b.responseId - a.responseId);
            const response =
              newest.find((row) =>
                ['MESSAGED', 'APPLIED', 'NEGOTIATING', 'PENDING_ACCEPTANCE'].includes(row.status)
              ) ?? newest[0];
            return response
              ? {
                  responseId: response.responseId,
                  status: response.status,
                  latestNegotiation: response.latestNegotiation
                    ? { id: response.latestNegotiation.id }
                    : null,
                }
              : null;
          }
          const result = await api.getThread(input.responseId, requestSignal);
          if (
            result.opportunity.responseId !== input.responseId ||
            result.opportunity.listing.id !== input.listingId
          ) {
            throw new Error('Service response scope changed.');
          }
          return result.opportunity;
        },
        MARKET_READ_TIMEOUT_MS,
        signal
      )
    ),
  },
  actions: {
    settled: assign({
      open: false,
      uncertain: false,
      attempt: null,
      error: null,
      revision: ({ context }) => context.revision + 1,
    }),
    discardDiscovery: assign({
      attempt: null,
      recoveredResponseId: null,
      replayResponseId: null,
      discoveryResetRevision: ({ context }) => context.discoveryResetRevision + 1,
      confirmedResponseId: null,
      uncertain: false,
      error: null,
      revision: ({ context }) => context.revision + 1,
    }),
  },
}).createMachine({
  id: 'serviceBegin',
  initial: 'idle',
  context: ({ input }) => ({
    responseId: 'responseId' in input ? input.responseId : null,
    discoveryListingId: 'listingId' in input ? input.listingId : null,
    recoveredResponseId: null,
    replayResponseId: null,
    discoveryResetRevision: 0,
    confirmedResponseId: null,
    ...initialAvailabilityWindow(),
    source: null,
    listing: null,
    selectedKeys: [],
    attempt: null,
    open: false,
    error: null,
    uncertain: false,
    revision: 0,
  }),
  on: {
    CLOSE: { actions: assign({ open: false }) },
    OPEN: {
      guard: ({ context, event }) => event.opportunity.responseId === context.responseId,
      actions: assign({ open: true }),
    },
  },
  states: {
    idle: {
      on: {
        BEGIN_DISCOVERY: {
          guard: ({ context, event }) =>
            context.discoveryListingId === event.listing.providerServiceListingId &&
            event.listing.existingResponseId == null &&
            context.confirmedResponseId === null &&
            selectedAvailabilityEntryIds(event.listing, new Set(event.selectedKeys)).length > 0,
          target: 'sending',
          actions: assign({
            attempt: ({ event }) => ({
              responseId: null,
              listingId: event.listing.providerServiceListingId,
              entryIds: selectedAvailabilityEntryIds(event.listing, new Set(event.selectedKeys)),
            }),
            recoveredResponseId: null,
          }),
        },
        DISCARD: {
          guard: ({ context }) => context.discoveryListingId !== null,
          actions: 'discardDiscovery',
        },
        // [MCV-1951] Sent by a search row whose fresh data shows no live response for a kept
        // confirmation (it turned terminal). The receipt has no Dismiss any more, and a kept
        // confirmation would block BEGIN_DISCOVERY.
        CLEAR_CONFIRMATION: {
          guard: ({ context }) => context.confirmedResponseId !== null,
          // A recovered id must not be replayed into the next Begin (DISCARD clears it too).
          actions: assign({ confirmedResponseId: null, replayResponseId: null }),
        },
        OPEN: [
          {
            guard: ({ context, event }) =>
              event.opportunity.responseId === context.responseId &&
              event.opportunity.viewerRole === 'RESPONDER' &&
              event.opportunity.latestNegotiation === null &&
              event.opportunity.availableActions.includes('counter'),
            target: 'loading',
            actions: assign({ source: ({ event }) => event.opportunity, open: true }),
          },
          {},
        ],
      },
    },
    loading: {
      entry: assign({ listing: null, selectedKeys: [], error: null }),
      invoke: {
        src: 'search',
        input: ({ context }) => ({
          start: context.start,
          // [MCV-1837] Part-time and full-time listings are searched by start date alone, like
          // Search Providers: with no end the API looks a year ahead, so a first opening more than
          // thirty days out is still found.
          end: isStartDateOnly(context.source?.listing.listingKind ?? '') ? undefined : context.end,
          listingId: context.source?.listing.id ?? null,
        }),
        onDone: {
          target: 'ready',
          actions: assign({
            listing: ({ event }) => event.output,
            // [MCV-1837] Part-time and full-time listings are offered by start date: a single
            // option on the earliest date is selected for the provider; several overlapping
            // ones are left for the provider to choose between (CHOOSE).
            selectedKeys: ({ event }) =>
              event.output && isStartDateOnly(event.output.listingKind)
                ? autoSelectedStartKeys(event.output.availability?.selectionOptions ?? [])
                : [],
          }),
        },
        onError: {
          target: 'ready',
          actions: assign({ error: 'Unable to load availability. Search again to retry.' }),
        },
      },
    },
    ready: {
      on: {
        SEARCH: {
          guard: ({ context, event }) =>
            /^\d{4}-\d{2}-\d{2}$/.test(event.window.start) &&
            // The end date does not apply to part-time and full-time listings.
            (isStartDateOnly(context.source?.listing.listingKind ?? '') ||
              (/^\d{4}-\d{2}-\d{2}$/.test(event.window.end) &&
                event.window.start <= event.window.end)),
          target: 'loading',
          actions: assign({
            start: ({ event }) => event.window.start,
            end: ({ event }) => event.window.end,
          }),
        },
        TOGGLE: {
          actions: assign({
            selectedKeys: ({ context, event }) =>
              context.selectedKeys.includes(event.key)
                ? context.selectedKeys.filter((key) => key !== event.key)
                : [...context.selectedKeys, event.key],
          }),
        },
        CHOOSE: {
          // Radio semantics for the start-date-only choice: one option, from the earliest date.
          guard: ({ context, event }) =>
            context.listing !== null &&
            isStartDateOnly(context.listing.listingKind) &&
            earliestDateOptions(context.listing.availability?.selectionOptions ?? [])
              .map(selectionOptionKey)
              .includes(event.key),
          actions: assign({ selectedKeys: ({ event }) => [event.key] }),
        },
        BEGIN: {
          guard: ({ context }) =>
            context.listing !== null &&
            selectedAvailabilityEntryIds(context.listing, new Set(context.selectedKeys)).length > 0,
          target: 'sending',
          actions: assign({
            attempt: ({ context }) =>
              context.listing && context.source
                ? {
                    responseId: context.responseId,
                    listingId: context.source.listing.id,
                    entryIds: selectedAvailabilityEntryIds(
                      context.listing,
                      new Set(context.selectedKeys)
                    ),
                  }
                : null,
          }),
        },
      },
    },
    sending: {
      entry: assign({ error: null }),
      on: { CLOSE: {}, OPEN: {} },
      invoke: {
        src: 'begin',
        input: ({ context }) =>
          context.attempt && {
            ...context.attempt,
            responseId: context.attempt.responseId ?? context.replayResponseId,
          },
        onDone: {
          target: 'idle',
          actions: [
            'settled',
            assign({
              confirmedResponseId: ({ context, event }) =>
                context.discoveryListingId === null ? null : event.output.jobResponseId,
            }),
          ],
        },
        onError: {
          target: 'recovering',
          actions: assign({
            uncertain: ({ context, event }) =>
              context.uncertain || isUncertainListingActionError(event.error),
            error: 'Begin was not confirmed. Checking the response before another action.',
          }),
        },
      },
    },
    recovering: {
      on: { CLOSE: {}, OPEN: {} },
      invoke: {
        src: 'recover',
        input: ({ context }) => context.attempt,
        onDone: [
          {
            guard: ({ context }) => context.discoveryListingId !== null,
            target: 'discoveryReview',
            actions: assign({
              recoveredResponseId: ({ event }) => event.output?.responseId ?? null,
              replayResponseId: ({ event }) =>
                event.output &&
                ['MESSAGED', 'APPLIED', 'NEGOTIATING', 'PENDING_ACCEPTANCE'].includes(
                  event.output.status
                )
                  ? event.output.responseId
                  : null,
            }),
          },
          {
            guard: ({ event }) =>
              event.output !== null &&
              (event.output.latestNegotiation !== null || event.output.status !== 'MESSAGED'),
            target: 'idle',
            actions: 'settled',
          },
          { guard: ({ context }) => context.uncertain, target: 'uncertain' },
          {
            target: 'ready',
            actions: assign({
              listing: null,
              selectedKeys: [],
              attempt: null,
              error: 'The offer was not started. Search current availability before trying again.',
            }),
          },
        ],
        onError: [
          {
            guard: ({ context }) => context.discoveryListingId !== null,
            target: 'discoveryReview',
          },
          { target: 'uncertain', actions: assign({ uncertain: true }) },
        ],
      },
    },
    discoveryReview: {
      entry: assign({
        error: ({ context }) =>
          context.uncertain
            ? 'We could not confirm whether this offer started. Review the response or explicitly retry the original selection.'
            : 'The offer was not started. Review the response or current availability before trying again.',
      }),
      on: {
        RETRY: { guard: ({ context }) => context.uncertain, target: 'sending' },
        REFRESH: 'recovering',
        DISCARD: { target: 'idle', actions: 'discardDiscovery' },
      },
    },
    uncertain: {
      entry: assign({
        error:
          'We could not confirm whether the offer started. Refresh the response or explicitly retry the original selection; no retry happens automatically.',
      }),
      on: {
        RETRY: 'sending',
        REFRESH: 'recovering',
        DISCARD: {
          target: 'idle',
          actions: assign({
            attempt: null,
            selectedKeys: [],
            listing: null,
            source: null,
            uncertain: false,
            open: false,
            error: null,
          }),
        },
      },
    },
  },
});
